import { ReactNode } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { uniq } from 'lodash'
import { faX } from '@fortawesome/pro-regular-svg-icons'
import { TaxSeasonKickoffSurveyProps } from './YearEndKickoffSurvey'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import {
  Card,
  CardColors,
  Form,
  Icon,
  Image,
  List,
  RadioGroupItem,
  Text,
} from '@/ui'
import { TaxFormType } from '../questionnaires/constants'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectCurrentAnnualTaxDetails,
  selectCurrentAnnualTaxYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  selectCurrentAnnualTaxFiling,
  selectTskIsScorp,
} from '../annualTaxFilings.selector'
import { TAX_SEASON_KICKOFF_SURVEY } from './TskTipsSideBar'
import { selectPersonalFilingIncludedForFree } from '../tax-checklist/taxChecklist.selectors'
import { getFieldNames, makeReqBoolSchema, makeReqStringSchema } from '@/base'
import { TypographyStyles } from '@/styles'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

const CheckListItem = ({
  title,
  description,
  extraContent,
}: {
  title: string
  description: string
  extraContent?: ReactNode
}) => (
  <List.Item
    customIcon={<Icon icon={faX} color="text-yellow-200" />}
    className="mb-4"
  >
    <div className="flex flex-col gap-1">
      <Text variant={TypographyStyles.H3}>{title}</Text>
      <Text variant={TypographyStyles.BODY_SM} className="mt-2">
        {description}
      </Text>
      {extraContent && <div className="mt-3">{extraContent}</div>}
    </div>
  </List.Item>
)

const TaxConfirmation = ({
  goToNextStep,
  goBack,
}: TaxSeasonKickoffSurveyProps) => {
  const currentAnnualTaxFiling = useReselector(selectCurrentAnnualTaxFiling)
  const currentAnnualTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const isSCorporation = useReselector(selectTskIsScorp)
  const annualTaxDetails = useReselector(selectCurrentAnnualTaxDetails)

  const hasFree2ndFilingPermission = useReselector(
    selectPersonalFilingIncludedForFree
  )

  const formik = useFormik({
    initialValues: {
      form1040Selected: undefined as 'true' | 'false' | undefined,
      confirmed: false,
    },
    onSubmit: ({ form1040Selected }) => {
      let selectedForms = [
        ...(currentAnnualTaxFiling?.annualTaxFormNeeds ?? []),
      ]

      if (form1040Selected === 'true') {
        selectedForms.push(TaxFormType.form1040)
      } else if (form1040Selected === 'false') {
        selectedForms = selectedForms.filter(
          (item) => item !== TaxFormType.form1040
        )
      }

      goToNextStep(
        { annualTaxFormNeeds: uniq(selectedForms) },
        TAX_SEASON_KICKOFF_SURVEY.review
      )
    },
  })

  const { submitForm, isSubmitting, values } = formik
  const fieldNames = getFieldNames(formik)

  const form1120SOptInCutOff = convertUtcToLocalDate(
    annualTaxDetails?.form_1120_s_tq_due_date
  )?.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)
  const forcedExtensionDate = convertUtcToLocalDate(
    annualTaxDetails?.tax_entity_verification_due_date
  )?.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)

  return (
    <FormikProvider value={formik}>
      <div className="mt-10 flex justify-center">
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/coffee.svg"
          alt="coffee"
          width={180}
          height={180}
        />
      </div>
      <Text variant={TypographyStyles.H1} textAlign="center" className="mt-6">
        What to expect for {currentAnnualTaxYear} taxes
      </Text>
      <Text className="mt-6" variant={TypographyStyles.BODY_MD}>
        To help you prepare, here are the services not currently included with
        your subscription.
      </Text>
      <Card className="mt-6" color={CardColors.LEVEL_1}>
        <List>
          {isSCorporation && !hasFree2ndFilingPermission && (
            <CheckListItem
              title="Personal tax filing (Form 1040)"
              description="Federal individual income tax return for you and your spouse (if applicable)."
            />
          )}
          <CheckListItem
            title="Amended returns"
            description="An amended return is a corrected version of a tax return after the original return was submitted."
          />
        </List>
      </Card>

      {isSCorporation && !hasFree2ndFilingPermission && (
        <>
          <Text className="mt-10" variant={TypographyStyles.H2}>
            Your tax needs
          </Text>
          <Text variant={TypographyStyles.BODY_MD} className="mt-2">
            While personal taxes aren’t part of your current plan, you can save
            time and effort by having Heard handle them for an added fee ($450).
          </Text>
          <div className="mt-10">
            <Form.RadioGroup
              name={fieldNames.form1040Selected}
              label="Would you like Heard to file your personal taxes as well?"
              schema={makeReqStringSchema()}
            >
              <RadioGroupItem
                label="Yes, also file my personal taxes with Heard"
                value="true"
              />
              {values.form1040Selected === 'true' && (
                <Card color={CardColors.LEVEL_1}>
                  <Text variant={TypographyStyles.BODY_SM}>
                    Thanks for letting us know. We will collect payment from you
                    after you have completed your tax checklist.
                  </Text>
                </Card>
              )}
              <RadioGroupItem
                label="No, I’ll take care of my personal taxes myself"
                value="false"
              />
              {values.form1040Selected === 'false' && (
                <Card className="mt-6" color={CardColors.LEVEL_1}>
                  <Text>
                    Note: Even though you have decided not to file your Personal
                    tax filing (Form 1040) with us, keep in mind that you will
                    still need to work with a different tax preparer to file
                    this with the IRS.
                    <br />
                    <br />
                    Opt back in through the Annual Tax Center by{' '}
                    {form1120SOptInCutOff} before you complete your business tax
                    questionnaire.
                    <br />
                    <br />
                    If you opt back in after {forcedExtensionDate}, we may need
                    to file an extension.
                  </Text>
                </Card>
              )}
            </Form.RadioGroup>
          </div>
        </>
      )}

      <div className="mt-10">
        <Form.Checkbox
          label="I confirm that I have read and understand the scope of Heard's tax preparation services."
          name={fieldNames.confirmed}
          schema={makeReqBoolSchema({ trueOnly: true })}
        />
      </div>

      <FormFlowFooter
        onBack={() => goBack(TAX_SEASON_KICKOFF_SURVEY.taxNeeds)}
        onForward={submitForm}
        continueDisabled={isSubmitting}
        loading={isSubmitting}
      />
    </FormikProvider>
  )
}

export default TaxConfirmation
